import React, { useState, useCallback } from 'react';

interface Sponsor {
  id?: string;
  name: string;
  description: string;
  website: string;
  logoUrl: string;
  tier: 'platinum' | 'gold' | 'silver' | 'bronze' | 'partner';
  contribution: number;
}

interface SponsorManagementProps {
  sponsors: Sponsor[];
  onCreateSponsor: (sponsor: Omit<Sponsor, 'id'>) => Promise<void>;
  onUpdateSponsor: (sponsor: Sponsor) => Promise<void>;
  onDeleteSponsor: (id: string) => Promise<void>;
}

// Move SponsorForm outside the SponsorManagement component to prevent recreation on every render
interface SponsorFormProps {
  sponsor: Partial<Sponsor>;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  submitLabel: string;
  onSponsorUpdate: (field: string, value: any) => void;
  onCancel: () => void;
}

const SponsorForm: React.FC<SponsorFormProps> = React.memo(({ 
  sponsor, 
  onSubmit, 
  submitLabel, 
  onSponsorUpdate,
  onCancel
}) => {
  const handleChange = useCallback((field: string, value: any) => {
    onSponsorUpdate(field, value);
  }, [onSponsorUpdate]);

  return (
    <form onSubmit={onSubmit} className="space-y-4 p-4 bg-gray-50 rounded-lg">
      <div>
        <label className="block text-sm font-medium text-gray-700">Name</label>
        <input
          type="text"
          value={sponsor.name || ''}
          onChange={(e) => handleChange('name', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">Description</label>
        <textarea
          value={sponsor.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          rows={3}
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Website</label>
          <input
            type="url"
            value={sponsor.website || ''}
            onChange={(e) => handleChange('website', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Logo URL</label>
          <input
            type="url"
            value={sponsor.logoUrl || ''}
            onChange={(e) => handleChange('logoUrl', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Tier</label>
          <select
            value={sponsor.tier || 'gold'}
            onChange={(e) => handleChange('tier', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          >
            <option value="platinum">Platinum</option>
            <option value="gold">Gold</option>
            <option value="silver">Silver</option>
            <option value="bronze">Bronze</option>
            <option value="partner">Partner</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Contribution ($)</label>
          <input
            type="number"
            value={sponsor.contribution || ''}
            onChange={(e) => handleChange('contribution', Number(e.target.value))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            min="0"
            step="100"
            required
          />
        </div>
      </div>
      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
});

const SponsorManagement: React.FC<SponsorManagementProps> = ({
  sponsors,
  onCreateSponsor,
  onUpdateSponsor,
  onDeleteSponsor,
}) => {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState<Sponsor | null>(null);
  const [newSponsor, setNewSponsor] = useState<Omit<Sponsor, 'id'>>({
    name: '',
    description: '',
    website: '',
    logoUrl: '',
    tier: 'gold',
    contribution: 0,
  });

  // Base64 placeholder image - a simple gray square with a logo icon
  const placeholderImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIGZpbGw9IiNFNUU3RUIiLz48cGF0aCBkPSJNMjQgMTZDMjAuNjg2MyAxNiAxOCAxOC42ODYzIDE4IDIyQzE4IDI1LjMxMzcgMjAuNjg2MyAyOCAyNCAyOEMyNy4zMTM3IDI4IDMwIDI1LjMxMzcgMzAgMjJDMzAgMTguNjg2MyAyNy4zMTM3IDE2IDI0IDE2Wk0yNCAyNkMyMS43OTA5IDI2IDIwIDI0LjIwOTEgMjAgMjJDMjAgMTkuNzkwOSAyMS43OTA5IDE4IDI0IDE4QzI2LjIwOTEgMTggMjggMTkuNzkwOSAyOCAyMkMyOCAyNC4yMDkxIDI2LjIwOTEgMjYgMjQgMjZaIiBmaWxsPSIjOTA5NUEwIi8+PC9zdmc+';

  const handleSubmitNew = async (e: React.FormEvent) => {
    e.preventDefault();
    await onCreateSponsor(newSponsor);
    setIsAddingNew(false);
    setNewSponsor({
      name: '',
      description: '',
      website: '',
      logoUrl: '',
      tier: 'gold',
      contribution: 0,
    });
  };

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingSponsor) {
      await onUpdateSponsor(editingSponsor);
      setEditingSponsor(null);
    }
  };

  const handleUpdateNewSponsor = useCallback((field: string, value: any) => {
    setNewSponsor(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleUpdateEditingSponsor = useCallback((field: string, value: any) => {
    setEditingSponsor(prev => prev ? { ...prev, [field]: value } : null);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Sponsor Management</h2>
        <button
          onClick={() => setIsAddingNew(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          Add Sponsor
        </button>
      </div>

      {isAddingNew && (
        <SponsorForm
          sponsor={newSponsor}
          onSubmit={handleSubmitNew}
          submitLabel="Create Sponsor"
          onSponsorUpdate={handleUpdateNewSponsor}
          onCancel={() => setIsAddingNew(false)}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sponsors.map((sponsor) => (
          <div
            key={sponsor.id}
            className="bg-white shadow rounded-lg p-4"
          >
            {editingSponsor?.id === sponsor.id ? (
              <SponsorForm
                sponsor={editingSponsor || {}}
                onSubmit={handleSubmitEdit}
                submitLabel="Update Sponsor"
                onSponsorUpdate={handleUpdateEditingSponsor}
                onCancel={() => setEditingSponsor(null)}
              />
            ) : (
              <div>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <img
                        src={sponsor.logoUrl || placeholderImage}
                        alt={`${sponsor.name} logo`}
                        className="h-12 w-12 object-contain bg-gray-50 rounded"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = placeholderImage;
                        }}
                      />
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{sponsor.name}</h3>
                        <p className="text-sm text-gray-500">
                          {sponsor.tier ? `${sponsor.tier.charAt(0).toUpperCase()}${sponsor.tier.slice(1)} Tier` : 'No Tier'}
                        </p>
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">{sponsor.description || 'No description available'}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditingSponsor(sponsor)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onDeleteSponsor(sponsor.id!)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="mt-4 flex justify-between text-sm text-gray-500">
                  <a
                    href={sponsor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Visit Website
                  </a>
                  <span>Contribution: ${sponsor.contribution.toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SponsorManagement;
